import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { DeepReadonly } from '@tradecafe/types/utils'
import { difference } from 'lodash-es'
import { from } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { DocumentApiService } from 'src/api/document-lib/document'
import { DocumentSetApiService } from 'src/api/document-lib/document-set'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { createDocumentSet, createDocumentSetFailure, createDocumentSetSuccess, deleteDocumentSet, deleteDocumentSetFailure, deleteDocumentSetSuccess, loadDocumentSets, loadDocumentSetsFailure, loadDocumentSetsSuccess, updateDocumentSet, updateDocumentSetFailure, updateDocumentSetSuccess } from './document-set.actions'

@Injectable()
export class DocumentSetEffects {
  private actions$ = inject(Actions)
  private DocumentApi = inject(DocumentApiService)
  private DocumentSetApi = inject(DocumentSetApiService)
  private toaster = inject(ToasterService)


  loadDocumentSets$ = createEffect(() => this.actions$.pipe(
    ofType(loadDocumentSets),
    switchMap(action =>
      from(this.DocumentSetApi.list()).pipe(
        map(r => loadDocumentSetsSuccess({
          tableKey: action.tableKey,
          documentSets: r.data,
        })),
        catchAndLog(this.toaster, 'Unable to fetch documentSets', loadDocumentSetsFailure
      )))))

  createDocumentSet$ = createEffect(() => this.actions$.pipe(
    ofType(createDocumentSet),
    tap(() => this.toaster.progress('Creating document set...')),
    switchMap(action =>
      from(this.DocumentSetApi.create(action.set).then(({ data }) =>
        this.updateDocumentsForDocumentSet(data.set_id, action.set.docs, []).then(() => data)
      )).pipe(
        map(r => createDocumentSetSuccess({ set: r })),
        tap(() => this.toaster.success('Document Set created successfully.')),
        catchAndLog(this.toaster, 'Unable to create document set', createDocumentSetFailure
      )))))

  updateDocumentSet$ = createEffect(() => this.actions$.pipe(
    ofType(updateDocumentSet),
    tap(() => this.toaster.progress('Updating document set...')),
    switchMap(action =>
      from(Promise.all([
        this.DocumentSetApi.update(action.id, action.set),
        this.updateDocumentsForDocumentSet(action.id, action.set.docs, action.original.docs),
      ])).pipe(
        map(([r]) => updateDocumentSetSuccess({ set: r.data })),
        tap(() => this.toaster.success('Document Set updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update document set', updateDocumentSetFailure
      )))))

  deleteDocumentSet$ = createEffect(() => this.actions$.pipe(
    ofType(deleteDocumentSet),
    tap(() => this.toaster.progress('Deleting document set...')),
    switchMap(action =>
      this.DocumentSetApi.delete(action.set.set_id).pipe(
        map(() => deleteDocumentSetSuccess({ id: action.id })),
        tap(() => this.toaster.success('Document Set deleted successfully.')),
        catchAndLog(this.toaster, 'Unable to delete document set', deleteDocumentSetFailure
      )))))

  private updateDocumentsForDocumentSet(setId: string, newDocs: DeepReadonly<string[]>, oldDocs: DeepReadonly<string[]>) {
    const added = difference(newDocs, oldDocs)
    const removed = difference(oldDocs, newDocs)

    return Promise.all([
      ...added.map(docId => this.DocumentApi.addSet(docId, setId)
        .catch((err) => { if (err.status !== 404) throw err })),
      ...removed.map(docId => this.DocumentApi.removeSet(docId, setId)
        .catch((err) => { if (err.status !== 404) throw err })),
    ])
  }
}
